import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { CircularProgress, Grid } from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adaptDireccionPaciente } from '../../../../adapters/contrato/direccionPacienteAdapter';
import { CustomButton } from '../../../../components/customButton/CustomButton';
import { RenderInputsRowByType } from '../../../../components/formGroupGenerator/RenderInputRowByType';
import { CREATE_AND_UPDATE_EVALUACION_PACIENTE } from "../../../../data/graphQL/mutations/Contratos/DireccionPaciente/CreateAndUpdateEvaluacionPaciente";
import { adaptLugarServicioArray, GET_ALL_LUGAR_SERVICIO } from '../../../../data/graphQL/queries/Contratos/DatosPrincipales/getLugarServicio';
import { adaptPacienteActual, GET_DIRECCION_PACIENTE_BY_ID } from '../../../../data/graphQL/queries/Contratos/DireccionPaciente/getDatosEvaluacion';
import { adaptInstitucion, GET_INSTITUCION } from '../../../../data/graphQL/queries/Contratos/DireccionPaciente/getInstitucion';
import { adaptPacienteSAP, GET_PACIENTE_SAP } from '../../../../data/graphQL/queries/Contratos/DireccionPaciente/getPacienteSAP';
import { GET_PACIENTE_SAP_BY_CODIGO } from '../../../../data/graphQL/queries/Contratos/DireccionPaciente/getPacienteSAPByCodigo';
import { deepEqualObj } from '../../../../utils/helpers';
import { GroupedContainer } from '../GroupedContainer';
import { GeoPosicionamiento } from './components/GeoPosicionamiento';
import { PacienteSAPInput } from './components/PacienteSAPInput';

export const hasMissingInstitucionAddressFields = (direccionObject) => {
  return !direccionObject?.direccionInstitucion ||
    !direccionObject?.localidadInstitucion ||
    !direccionObject?.provinciaInstitucion ||
    !direccionObject?.codigoPostalInstitucion;
};

export const TabDireccionPaciente = ({
  contratoEditable,
  handleChangeDireccionPaciente,
  origen,
  setDataSubmissionCallback,
  stepData,
  fullFormData,
  idDelegacion,
  idEvaluacion,
  codigoDelegacion,
  pageLoading,
  setPageLoading,
  cambioDireccion,
  setCambioDireccion
}) => {
  const [direccionPaciente, setDireccionPaciente] = useState({});
  const [lugarServicioCombo, setLugarServicioCombo] = useState([]);
  const [institucionDireccionModel, setInstitucionDireccionModel] = useState({});
  const [pacienteSearchDireccion, setPacienteSearchDireccion] = useState(false);
  const [temporaryDomicilioPacienteSAP, setTemporaryDomicilioPacienteSAP] = useState(null);

  const [idPaciente, codInstitucion] = useMemo(() => [
    fullFormData?.datosPrincipales?.idPaciente || 0,
    fullFormData?.datosPrincipales?.idInstitucion || ''],
    [
      fullFormData?.datosPrincipales?.idPaciente,
      fullFormData?.datosPrincipales?.idInstitucion]
  );

  const [lugarInternacionId, lugarDomicilioId] = useMemo(() => [
    lugarServicioCombo?.find((x) => x.displayName === 'Internación')?.id,
    lugarServicioCombo?.find((x) => x.displayName === 'Domicilio')?.id],
    [
      lugarServicioCombo]
  );

  const [isInstitucion, isDomicilio] = useMemo(() => [
    lugarInternacionId === direccionPaciente.idLugarRI,
    lugarDomicilioId === direccionPaciente.idLugarRI],
    [
      lugarInternacionId,
      direccionPaciente.idLugarRI]
  );

  const handleChange = (name, value) => {
    setDireccionPaciente((prevDatos) => ({
      ...prevDatos,
      [name]: value
    }));
  };

  useEffect(() => {
    setDireccionPaciente((prevDatos) => ({
      ...prevDatos,
      ...fullFormData.direccionPaciente
    }));
  }, [fullFormData.direccionPaciente]);

  const handleUpdateDireccionPaciente = (pacienteSAPData, lugarDomicilioData) => {
    const valorDomicilioCombo = lugarServicioCombo?.find((x) => x.displayName === 'Domicilio')?.id;
    const isDomicilio = [pacienteSAPData?.idLugarServicio, direccionPaciente?.idLugarRI].includes(valorDomicilioCombo);

    setTemporaryDomicilioPacienteSAP({ ...lugarDomicilioData });
    if (!direccionPaciente.longitud || direccionPaciente.longitud == "" || !direccionPaciente.latitud || direccionPaciente.longitud == "") {
      setCambioDireccion(true);
    }

    const auxDireccion = {
      ...fullFormData?.direccionPaciente,
      ...pacienteSAPData,
      ...(isDomicilio && lugarDomicilioData)
    };

    handleChangeDireccionPaciente(auxDireccion);
  };

  const { data: dataLugarServicio } = useQuery(GET_ALL_LUGAR_SERVICIO, {
    fetchPolicy: 'no-cache'
  }, []);

  useEffect(() => {
    if (!dataLugarServicio) return;
    setLugarServicioCombo(adaptLugarServicioArray(dataLugarServicio?.allLugarServicio));
  }, [dataLugarServicio]);

  const [getPacienteSAPByCodigo, { data: dataPacientesSAP, loading: pacienteSAPByCodigoLoading }] = useLazyQuery(GET_PACIENTE_SAP_BY_CODIGO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataPacientesSAP) return;
    if (dataPacientesSAP?.pacienteSAPByCodigo?.error) {
      toast.error(dataPacientesSAP?.pacienteSAPByCodigo?.error, { duration: 10000 });
      return;
    }
    if (!dataPacientesSAP?.pacienteSAPByCodigo?.success) {
      toast.error('Error al buscar el paciente SAP');
      return;
    }

    const adaptedPaciente = adaptPacienteSAP(dataPacientesSAP?.pacienteSAPByCodigo?.valorEsperado);

    handleUpdateDireccionPaciente(adaptedPaciente, {
      direccionInstitucion: adaptedPaciente?.direccion,
      provinciaInstitucion: adaptedPaciente?.provincia,
      localidadInstitucion: adaptedPaciente?.localidad,
      codigoPostalInstitucion: adaptedPaciente?.cP
    });
  }, [dataPacientesSAP]);

  const [getPacienteSAP] = useLazyQuery(GET_PACIENTE_SAP, {
    fetchPolicy: 'no-cache'
  });

  const [getContratoActual] = useLazyQuery(GET_DIRECCION_PACIENTE_BY_ID, {
    fetchPolicy: 'no-cache'
  });

  const fetchContratoActual = async () => {
    try {
      const { data } = await getContratoActual({ variables: { idEvaluacion } });

      if (data?.evaluacionById?.evaluacion) {
        const datosActuales = adaptPacienteActual(data.evaluacionById.evaluacion);

        handleChange('observacionExtra', datosActuales?.direccionDelPaciente?.observacion);
        handleChange('latitud', datosActuales?.latitud);
        handleChange('longitud', datosActuales?.longitud);
        handleChangeIdLugarRI(datosActuales?.idLugarRI);
        handleChangeDireccionPaciente(datosActuales.direccionInstitucion);

        if (
          lugarDomicilioId === datosActuales?.idLugarRI &&
          hasMissingInstitucionAddressFields(datosActuales?.direccionInstitucion)
        ) {
          setTemporaryDomicilioPacienteSAP(datosActuales.direccionInstitucion);
        }
      }
    } catch (err) {
      console.error("Error contrato:", err);
    }
  };

  const fetchPacienteSAP = async () => {
    if (!fullFormData?.datosPrincipales?.codSAPPaciente) {
      setPacienteSearchDireccion(true);
      return;
    }

    try {
      const { data } = await getPacienteSAP({
        variables: {
          codigoSAP: fullFormData.datosPrincipales.codSAPPaciente,
          idEvaluacion
        }
      });

      if (data?.pacienteSAP) {
        setPacienteSearchDireccion(data.pacienteSAP.cod_Paciente === "");

        const pacienteSAP = adaptPacienteSAP(data.pacienteSAP);

        handleChangeDireccionPaciente({
          codigoSAP: pacienteSAP.codigoSAP,
          nombre: pacienteSAP.nombre,
          direccion: pacienteSAP.direccion,
          provincia: pacienteSAP.provincia,
          localidad: pacienteSAP.localidad,
          codigoPostal: pacienteSAP.codigoPostal
        });

        if (lugarDomicilioId === direccionPaciente?.idLugarRI) {
          handleChangeDireccionPaciente({
            direccionInstitucion: pacienteSAP.direccion,
            localidadInstitucion: pacienteSAP.localidad,
            provinciaInstitucion: pacienteSAP.provincia,
            codigoPostalInstitucion: pacienteSAP.codigoPostal
          });
        }
      }
    } catch (err) {
      console.error("Error SAP:", err);
    }
  };

  useEffect(() => {
    if (!idEvaluacion || !lugarInternacionId) return;
    if (fullFormData?.datosPrincipales?.codSAPPaciente === undefined) return;

    const fetchData = async () => {
      await fetchContratoActual();
      await fetchPacienteSAP();
    };

    fetchData();
  }, [
    origen,
    idEvaluacion,
    lugarInternacionId,
    fullFormData?.datosPrincipales?.codSAPPaciente
  ]);

  useEffect(() => {
    if (!codInstitucion || !idEvaluacion) return;
    getInstitucion({
      variables: {
        codigoInstitucion: codInstitucion,
        idEvaluacion: idEvaluacion
      }
    });
  }, [codInstitucion, idEvaluacion]);

  const [getInstitucion, { data: dataInstitucion }] = useLazyQuery(GET_INSTITUCION, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataInstitucion) return;
    const institucion = adaptInstitucion(dataInstitucion?.institucion);
    setInstitucionDireccionModel(institucion);
  }, [dataInstitucion]);

  const handleChangeIdLugarRI = (idLugarRI) => {
    if (!idLugarRI || idLugarRI === direccionPaciente.idLugarRI) return;
    let cambiosADireccionPaciente = { idLugarRI };
    if (lugarInternacionId === idLugarRI) {
      if (codInstitucion) {
        cambiosADireccionPaciente = {
          ...cambiosADireccionPaciente,
          ...institucionDireccionModel
        };
      } else {
        cambiosADireccionPaciente = {
          idLugarRI,
          direccionInstitucion: '',
          provinciaInstitucion: '',
          localidadInstitucion: '',
          codigoPostalInstitucion: '',
          habitacion: ''
        };
      }
    } else {
      cambiosADireccionPaciente = {
        idLugarRI,
        direccionInstitucion: direccionPaciente.direccion,
        provinciaInstitucion: direccionPaciente.provincia,
        localidadInstitucion: direccionPaciente.localidad,
        codigoPostalInstitucion: direccionPaciente.codigoPostal
      };
    }
    handleChangeDireccionPaciente(cambiosADireccionPaciente);
  };

  const [createAndUpdateEvaluacionPaciente] = useMutation(CREATE_AND_UPDATE_EVALUACION_PACIENTE);

  const handleSubmit = () => {
    setPageLoading(true);
    const inputDireccionPaciente = adaptDireccionPaciente(direccionPaciente, idEvaluacion, idPaciente);
    createAndUpdateEvaluacionPaciente({
      variables: {
        input: inputDireccionPaciente
      }
    }).then(() => {
      handleChangeDireccionPaciente(direccionPaciente);

      if (hasMissingInstitucionAddressFields(direccionPaciente)) {
        setTemporaryDomicilioPacienteSAP(null);
      }
    }).catch(() => {
      toast.error('Error al guardar dirección paciente');
    }).finally(() => {
      setPageLoading(false);
    });
  };

  const buscarPacienteSAPByCodigo = () => {
    getPacienteSAPByCodigo({
      variables: {
        codPaciente: direccionPaciente?.codigoSAPBuscar,
        idEvaluacion: idEvaluacion
      }
    });
  };

  const validatedinputsRows = useMemo(() => {
    const disabledFields = origen !== 'USD' && origen !== 'USN';

    return structuredClone(inputsRows).map((group) => ({
      ...group,
      rows: group.rows.map((row) => ({
        ...row,
        inputs: row.inputs.map((input) => {
          switch (input.name) {
            case 'vincularPacienteSAP':
              input.disabled = pacienteSearchDireccion ? false : !!direccionPaciente?.codigoSAP || origen?.trim() === 'PE';
              break;
            case 'codigoSAPBuscar':
              input.disabled = pacienteSearchDireccion ? false : !!direccionPaciente?.codigoSAP || origen?.trim() === 'PE';
              break;
            case 'idLugarRI':
              input.options = lugarServicioCombo.map((lugar) => ({ ...lugar, disabled: lugar.id === lugarDomicilioId && !direccionPaciente?.codigoSAP }));
              input.disabled = !direccionPaciente?.codigoSAP || !!direccionPaciente.idLugarRI && !!fullFormData.evaluacionAsistencial?.fechaNotifAsistencial;
              break;
            case 'direccion':
            case 'provincia':
            case 'codigoPostal':
            case 'localidad':
              input.disabled = disabledFields;
              break;
            case 'direccionInstitucion':
            case 'provinciaInstitucion':
            case 'codigoPostalInstitucion':
            case 'localidadInstitucion':
              input.disabled = isInstitucion ?
                !!direccionPaciente[input.name] && !!fullFormData.evaluacionAsistencial?.fechaNotifAsistencial :
                isDomicilio ?
                  !(!!temporaryDomicilioPacienteSAP && !temporaryDomicilioPacienteSAP[input.name]) :
                  true;
              break;
            case 'habitacion':
              input.disabled = !isInstitucion || !!fullFormData.direccionPaciente?.habitacion && !!fullFormData.evaluacionAsistencial?.fechaNotifAsistencial;
              break;
            default:
              break;
          }
          if (!contratoEditable || pacienteSAPByCodigoLoading) {
            input.disabled = true;
          }
          return input;
        })
      }))
    }));
  }, [
    stepData,
    direccionPaciente,
    lugarServicioCombo,
    isInstitucion,
    idEvaluacion,
    codInstitucion,
    contratoEditable,
    codigoDelegacion,
    idDelegacion,
    institucionDireccionModel,
    pacienteSAPByCodigoLoading,
    temporaryDomicilioPacienteSAP,
    fullFormData.evaluacionAsistencial?.fechaNotifAsistencial]
  );

  useEffect(() => {
    setDataSubmissionCallback(handleSubmit);
  }, [direccionPaciente, setDataSubmissionCallback, validatedinputsRows]);


  useEffect(() => {
    const timer = setTimeout(() => {
      if (deepEqualObj(fullFormData?.direccionPaciente, direccionPaciente)) return;
      handleChangeDireccionPaciente(direccionPaciente);
    }, 400);

    return () => clearTimeout(timer);
  }, [direccionPaciente]);

  if (pageLoading) {
    return (
      <div className='tab-loading-section-contrato'>
        <CircularProgress />
      </div>);

  }

  return <div>
    <Grid
      container
      columns={100}
      columnSpacing={3}
      rowSpacing={3}>

      {validatedinputsRows.map((group, groupIndex) => {
        return <GroupedContainer key={groupIndex} ungrouped={group.ungrouped} sectionTitle={group.name}>
          {group.rows.map((row) =>
            row.inputs.map((input, index) => {
              return (
                <Fragment key={`groupIndex-${groupIndex}-input-${input.name}-input-index-${index}}`}>
                  {input.name === 'vincularPacienteSAP' &&
                    <PacienteSAPInput
                      handleChange={handleUpdateDireccionPaciente}
                      prevValue={direccionPaciente}
                      input={input}
                      idDelegacion={idDelegacion}
                      codDelegacion={codigoDelegacion}
                      origen={origen}
                    />
                  }
                  {input.name === 'buscarPacienteSAP' &&
                    <Grid size={{ xs: 100, sm: 50, md: 20, lg: input.col }}>
                      <CustomButton
                        label={input.displayName}
                        onClick={buscarPacienteSAPByCodigo}
                        isLoading={pacienteSAPByCodigoLoading}
                        disabled={!pacienteSearchDireccion || !direccionPaciente?.codigoSAPBuscar?.trim()}
                        sx={{ width: 'fit-content', ...input.sx }}
                        loadingText="Buscando..." />

                    </Grid>
                  }
                  {input.name === 'geoPosicion' &&
                    <GeoPosicionamiento
                      idEvaluacion={idEvaluacion}
                      direccionPaciente={direccionPaciente}
                      handleChange={handleChange}
                      cambioDireccion={cambioDireccion}
                      setCambioDireccion={setCambioDireccion}
                      onlyView={!(contratoEditable && cambioDireccion)}
                    />

                  }
                  {
                    input.name !== 'vincularPacienteSAP' &&
                    input.name !== 'buscarPacienteSAP' &&
                    input.name !== 'geoPosicion' &&
                    <RenderInputsRowByType
                      key={`groupIndex-${groupIndex}-input-${input.name}-inputIndex-${index}}`}
                      input={input}
                      value={direccionPaciente[input.name]}
                      handleChange={input.name !== 'idLugarRI' ? handleChange : (_, value) => handleChangeIdLugarRI(value)} />

                  }
                </Fragment>);

            })
          )}
        </GroupedContainer>;
      })}
    </Grid>
  </div>;
};

const inputsRows = [
  {
    name: 'Lugar donde se presta servicio',
    rows: [
      {
        inputs: [
          {
            displayName: 'Vincular Paciente SAP',
            name: 'vincularPacienteSAP',
            type: 'search',
            col: 30
          },
          {
            displayName: 'Código SAP',
            name: 'codigoSAPBuscar',
            type: 'string',
            col: 20
          },
          {
            displayName: 'Buscar SAP',
            name: 'buscarPacienteSAP',
            type: 'button',
            col: 50,
            sx: { width: 'fit-content' }
          }]

      },
      {
        inputs: [
          {
            displayName: 'Direccion',
            name: 'direccion',
            type: 'string',
            col: 30
          },
          {
            displayName: 'Provincia',
            name: 'provincia',
            type: 'string',
            col: 20
          },
          {
            displayName: 'Cod postal',
            name: 'codigoPostal',
            type: 'string',
            col: 10
          },
          {
            displayName: 'Localidad',
            name: 'localidad',
            type: 'string',
            col: 20
          },
          {
            displayName: '',
            name: 'geoPosicion',
            type: 'search',
            col: 4
          },
          {
            displayName: 'item',
            name: 'item',
            required: true,
            type: 'item',
            col: 16
          }]

      },
      {
        inputs: [
          {
            displayName: 'Observación Extra',
            name: 'observacionExtra',
            type: 'long_string',
            col: 100
          }]

      }]

  },
  {
    name: 'Lugar de evaluacion (RI) del paciente',
    rows: [
      {
        inputs: [
          {
            displayName: 'Lugar',
            name: 'idLugarRI',
            type: 'select',
            col: 20
          },
          {
            displayName: 'Direccion',
            name: 'direccionInstitucion',
            type: 'string',
            col: 20
          },
          {
            displayName: 'Localidad',
            name: 'localidadInstitucion',
            type: 'string',
            col: 20
          },
          {
            displayName: 'Provincia',
            name: 'provinciaInstitucion',
            type: 'string',
            col: 20
          },
          {
            displayName: 'Cod postal',
            name: 'codigoPostalInstitucion',
            type: 'string',
            col: 10
          },
          {
            displayName: 'Habitación',
            name: 'habitacion',
            type: 'string',
            col: 10
          }]

      }]

  }];
